/**
 * ADMIN MODULE — SEO Rank
 * The article-side twin of the LinkedIn Flywheel. Four panels: Controls (per-agent
 * switches, cadence, keyword target, cost cap + meter), Keyword Opportunities,
 * Audits, and Article Queue (draft → approved → published, with HTML preview).
 * Reads/writes go through seoService. "Run" buttons create Paperclip issues for the
 * seo-rank agents. Realtime on seo_keywords + seo_articles keeps the UI live.
 *
 * Cost note: article writing is on the Claude subscription (~$0). The cost meter
 * tracks DataForSEO spend only (seo_api_usage), capped by daily_cost_cap.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../../shared/utils/supabaseClient.js";
import { findAgentByName, createIssue } from "../utils/paperclipClient.js";
import AdminLayout from "../components/AdminLayout.jsx";
import {
  listClusters,
  listKeywords, setKeywordStatus,
  listBriefs, setBriefStatus, deleteBrief,
  listAudits,
  listArticles, setArticleStatus, updateArticleContent, recordArticleLesson,
  listPerformance,
  getSettings, updateSettings, getCostMeter,
  blogUrl,
} from "../utils/seoService.js";

const RESEARCH_AGENT = "Keyword Researcher";
const BRIEF_AGENT = "Brief Generator";
const WRITER_AGENT = "Article Writer";
const PUBLISH_AGENT = "Publisher";
const RANK_AGENT = "Rank Tracker";

// §5 hard gates, in display order. Mirrors HARD_GATES in article_tools.py.
const HARD_GATES = [
  ["expertise", "Expertise"],
  ["intent_match", "Intent match"],
  ["no_slop", "No slop"],
  ["originality", "Originality"],
  ["on_page", "On-page"],
];

// Renders an article's §5 rubric result: a chip per hard gate (green pass / red
// fail) with the note on hover, plus a banner when any gate failed.
function QualityReport({ quality }) {
  if (!quality || !quality.gates) return null;
  const gates = quality.gates;
  const failed = HARD_GATES.filter(([k]) => !gates[k]?.pass).map(([, label]) => label);
  return (
    <div style={{ marginTop: 6 }}>
      {failed.length > 0 && (
        <div className="admin-row-meta" style={{ color: "var(--warn)", marginBottom: 4 }}>
          ⚠ Hard gate failed: {failed.join(", ")} — edit before approving.
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {HARD_GATES.map(([k, label]) => {
          const g = gates[k] || {};
          return (
            <span
              key={k}
              className={`admin-badge ${g.pass ? "social_proof" : "warm"}`}
              title={g.note || (g.pass ? "pass" : "fail")}
              style={g.pass ? undefined : { color: "var(--warn)" }}
            >
              {g.pass ? "✓" : "✗"} {label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function fmtDate(t) {
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}
const usd = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

// Create a Paperclip issue for one of the seo-rank agents; returns a status message.
async function runAgent(agentName, title, description) {
  const { data: agent, error: aErr } = await findAgentByName(agentName);
  if (aErr || !agent) return aErr || `${agentName} agent not found. Is Paperclip running?`;
  const { error } = await createIssue(title, description, agent.id);
  return error ? `Could not start: ${error}` : "Started — results will appear here shortly.";
}

/* ─────────────────────────── Controls + cost meter ───────────────────────── */
const AGENT_SWITCHES = [
  { key: "research_enabled", label: "Keyword Researcher" },
  { key: "audit_enabled", label: "SEO Auditor" },
  { key: "writer_enabled", label: "Article Writer" },
  { key: "publisher_enabled", label: "Publisher" },
];

function Controls({ meter }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    getSettings().then((d) => { if (alive) setS(d); }).catch((e) => setMsg(e.message));
    return () => { alive = false; };
  }, []);

  if (!s) return null;
  const set = (patch) => setS({ ...s, ...patch });

  async function save() {
    setBusy(true); setMsg(null);
    try {
      await updateSettings(s.id, {
        research_enabled: s.research_enabled, audit_enabled: s.audit_enabled,
        writer_enabled: s.writer_enabled, publisher_enabled: s.publisher_enabled,
        articles_per_week: s.articles_per_week, keyword_target: s.keyword_target,
        min_volume: s.min_volume, max_difficulty: s.max_difficulty,
        daily_cost_cap: s.daily_cost_cap, auto_generate: s.auto_generate,
        auto_generate_count: s.auto_generate_count,
      });
      setMsg("Saved.");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  const capHit = meter && s.daily_cost_cap > 0 && meter.today != null && meter.today >= s.daily_cost_cap;

  return (
    <section className="admin-card" style={{ marginBottom: 18 }}>
      <div className="admin-panel-head" style={{ cursor: "pointer", margin: 0 }} onClick={() => setOpen((o) => !o)}>
        <h2>⚙ SEO Rank Controls</h2>
        <span className="admin-panel-count">
          {AGENT_SWITCHES.filter((a) => s[a.key]).length}/4 agents on · {s.articles_per_week}/wk ·
          {" "}cap {usd(s.daily_cost_cap)}/day {open ? "▲" : "▼"}
        </span>
      </div>

      {/* Cost meter — always visible */}
      <div className="admin-metric-row" style={{ display: "flex", gap: 22, marginTop: 12, flexWrap: "wrap" }}>
        <div className="admin-metric"><span className="num" style={{ color: capHit ? "var(--warn)" : undefined }}>{meter ? usd(meter.today) : "…"}</span><span className="lbl">DataForSEO today</span></div>
        <div className="admin-metric"><span className="num">{meter ? usd(meter.week) : "…"}</span><span className="lbl">last 7 days</span></div>
        <div className="admin-metric"><span className="num">{usd(s.daily_cost_cap)}</span><span className="lbl">daily cap</span></div>
      </div>
      {capHit && <div className="admin-row-meta" style={{ color: "var(--warn)" }}>Daily cost cap reached — research/audit agents will pause until tomorrow or until you raise the cap.</div>}

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
          <div>
            <div className="admin-row-meta" style={{ marginBottom: 8 }}>Agents — turn each on or off.</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {AGENT_SWITCHES.map((a) => (
                <label key={a.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!s[a.key]} onChange={(e) => set({ [a.key]: e.target.checked })} style={{ width: "auto" }} />
                  {a.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <label className="admin-field" style={{ maxWidth: 160 }}><span>Articles / week</span>
              <input type="number" min={1} max={21} value={s.articles_per_week} onChange={(e) => set({ articles_per_week: Number(e.target.value) })} />
            </label>
            <label className="admin-field" style={{ maxWidth: 180 }}><span>Keywords / research run</span>
              <input type="number" min={5} max={100} value={s.keyword_target} onChange={(e) => set({ keyword_target: Number(e.target.value) })} />
            </label>
            <label className="admin-field" style={{ maxWidth: 160 }}><span>Min search volume</span>
              <input type="number" min={0} value={s.min_volume} onChange={(e) => set({ min_volume: Number(e.target.value) })} />
            </label>
            <label className="admin-field" style={{ maxWidth: 160 }}><span>Max difficulty (0–100)</span>
              <input type="number" min={0} max={100} value={s.max_difficulty} onChange={(e) => set({ max_difficulty: Number(e.target.value) })} />
            </label>
            <label className="admin-field" style={{ maxWidth: 170 }}>
              <span style={{ display: "flex", justifyContent: "space-between", gap: 6 }}><span>Daily cost cap (USD)</span></span>
              <input type="number" min={0} step="0.5" value={s.daily_cost_cap} onChange={(e) => set({ daily_cost_cap: Number(e.target.value) })} />
            </label>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
            <input type="checkbox" checked={s.auto_generate} onChange={(e) => set({ auto_generate: e.target.checked })} style={{ width: "auto" }} />
            Auto-generate a batch of articles
          </label>
          {s.auto_generate && (
            <label className="admin-field" style={{ maxWidth: 200, marginTop: -6 }}><span>Articles per run</span>
              <input type="number" min={1} max={10}
                value={s.auto_generate_count ?? 3}
                onChange={(e) => set({ auto_generate_count: Number(e.target.value) })} />
            </label>
          )}
          <div className="admin-row-meta" style={{ marginTop: -8 }}>
            {s.auto_generate
              ? `On — “Generate articles” below will write ${s.auto_generate_count ?? 3} article${(s.auto_generate_count ?? 3) === 1 ? "" : "s"} per run, working through the next ready briefs in the queue. Save to apply.`
              : "Off — “Generate article” below writes one article per click. Turn this on to write several per run."}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save settings"}</button>
            {msg && <span className="admin-panel-count">{msg}</span>}
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── Panel: Keyword Queue ─────────────────────────── */
// Buyer-intent is the primary signal (spec §2): rank low-volume/high-intent over
// high-volume/low-intent. This is the main human gate where strategy is enforced.
const CLUSTER_INTENT_BADGE = { transactional: "social_proof", commercial: "warm", informational: "" };

function intentTone(score) {
  if (score == null) return "";           // legacy row, not yet intent-scored
  if (score >= 70) return "social_proof"; // strong buyer intent
  if (score >= 50) return "warm";
  return "";
}

function KeywordQueue({ keywords, clusters, reload }) {
  const [seed, setSeed] = useState("");
  const [clusterId, setClusterId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const clusterName = (id) => clusters.find((c) => c.id === id)?.pillar_topic;
  const activeClusters = clusters.filter((c) => c.status === "active");

  async function researchCluster() {
    setBusy(true); setMsg(null);
    const c = clusters.find((x) => x.id === clusterId);
    const m = await runAgent(
      RESEARCH_AGENT,
      c ? `Research cluster: ${c.pillar_topic}` : "Research all active clusters",
      c
        ? `Run seo_tools.py research-cluster ${c.id} per your HEARTBEAT (pillar: "${c.pillar_topic}"), then report stored keywords, top-by-intent, and today's spend.`
        : "Run seo_tools.py research-cluster all per HEARTBEAT.md across the active pillar clusters. Report stored keywords + today's spend.",
    );
    setBusy(false); setMsg(m);
  }
  async function researchSeed() {
    if (!seed.trim()) return;
    setBusy(true); setMsg(null);
    const m = await runAgent(
      RESEARCH_AGENT, `Research keywords: ${seed}`,
      `Run seo_tools.py research "${seed}" (ad-hoc seed) per your HEARTBEAT, then report stored keywords + today's spend.`,
    );
    setBusy(false); setMsg(m);
  }
  async function act(id, status) {
    try { await setKeywordStatus(id, status); await reload(); } catch (e) { alert(e.message); }
  }

  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>Keyword Queue</h2><span className="admin-panel-count">{keywords.length} total · ranked by intent</span></div>

      {/* Cluster-driven discovery is the default path. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <select value={clusterId} onChange={(e) => setClusterId(e.target.value)} style={{ flex: "1 1 240px", minWidth: 0 }}>
          <option value="">All active clusters</option>
          {activeClusters.map((c) => (
            <option key={c.id} value={c.id}>{c.pillar_topic} · {c.intent}</option>
          ))}
        </select>
        <button className="btn" onClick={researchCluster} disabled={busy}>{busy ? "Starting…" : "🔍 Research cluster"}</button>
      </div>
      {/* Ad-hoc seed, secondary. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="…or an ad-hoc seed (not tied to a cluster)" style={{ flex: "1 1 220px", minWidth: 0 }} />
        <button className="secondary" onClick={researchSeed} disabled={busy || !seed.trim()}>Research seed</button>
      </div>
      {msg && <div className="admin-row-meta" style={{ marginBottom: 10 }}>{msg}</div>}

      {keywords.length === 0 ? (
        <div className="admin-empty">No keywords yet — research a cluster to fill the queue.</div>
      ) : (
        <div className="admin-list">
          {keywords.slice(0, 80).map((k) => (
            <div className="admin-row" key={k.id}>
              <div className="admin-row-top">
                <span className="admin-row-title">{k.keyword}</span>
                <span className="admin-row-meta">
                  <span className={`admin-badge ${intentTone(k.intent_score)}`}>intent {k.intent_score ?? "—"}</span>
                  {k.status !== "new" && <span className={`admin-badge ${k.status === "used" ? "social_proof" : "warm"}`}>{k.status}</span>}
                </span>
              </div>
              <div className="admin-row-meta">
                <span>vol {k.volume ?? "—"}</span><span>diff {k.difficulty ?? "—"}</span>
                <span>cpc {k.cpc != null ? `$${Number(k.cpc).toFixed(2)}` : "—"}</span>
                <span>opp {k.opportunity_score ?? "—"}</span>
                {k.cluster_id ? <span>◆ {clusterName(k.cluster_id) ?? "cluster"}</span> : <span>seed: {k.seed_keyword}</span>}
              </div>
              {(k.status === "new" || k.status === "approved") && (
                <div className="admin-row-actions">
                  {k.status === "new" && <button className="btn" onClick={() => act(k.id, "approved")}>Queue for article</button>}
                  {k.status === "approved" && <button className="secondary" onClick={() => act(k.id, "new")}>Unqueue</button>}
                  <button className="danger" onClick={() => act(k.id, "archived")}>Archive</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── Panel: Audits ─────────────────────────────────── */
function AuditPanel({ audits }) {
  const [open, setOpen] = useState(null);
  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>On-Page Audits</h2><span className="admin-panel-count">{audits.length} total</span></div>
      {audits.length === 0 ? (
        <div className="admin-empty">No audits yet. Assign the SEO Auditor a page + keyword.</div>
      ) : (
        <div className="admin-list">
          {audits.map((a) => {
            const expanded = open === a.id;
            const tags = a.generated_tags || {};
            return (
              <div className="admin-row clickable" key={a.id} onClick={() => setOpen(expanded ? null : a.id)}>
                <div className="admin-row-top">
                  <span className="admin-row-title" style={{ wordBreak: "break-all" }}>{a.url}</span>
                  {a.keyword && <span className="admin-badge">{a.keyword}</span>}
                </div>
                <div className="admin-row-meta"><span>{fmtDate(a.created_at)}</span></div>
                {expanded && (
                  <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>
                    {tags.title_tag && <div className="admin-kv"><div className="row"><span className="k">Title tag</span><span className="v">{tags.title_tag}</span></div>
                      {tags.meta_description && <div className="row"><span className="k">Meta desc</span><span className="v">{tags.meta_description}</span></div>}</div>}
                    {a.recommendations && <div className="admin-draft" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{a.recommendations}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── Panel: Content Pipeline ───────────────────────── */
// One row per keyword-brief, carried through its whole life on a single line:
//   brief → (writing) → needs review → approved → published → live rank.
// This one panel replaces the old separate Brief Queue, Article Review, and
// Performance panels so everything tied to one keyword stays together. A filter at
// the top scopes the list to any stage.

// A positions series → a compact rank summary (lower is better; null = >100).
function rankSummary(series) {
  const positions = series.map((s) => s.position);
  const latest = positions.length ? positions[positions.length - 1] : undefined;
  const first = positions.length ? positions[0] : undefined;
  const norm = (v) => (v == null ? 101 : v);
  const delta = first !== undefined && latest !== undefined ? norm(first) - norm(latest) : 0;
  let bucket = "tracking";
  if (latest != null && latest <= 10) bucket = "top10";
  else if (delta > 0) bucket = "climbing";
  else if (series.length >= 2 && (latest == null || latest > 20)) bucket = "stuck";
  return { positions, latest, delta, bucket, snapshots: series.length };
}
const RANK_BADGE = { top10: "social_proof", climbing: "warm", stuck: "", tracking: "" };
const posLabel = (p) => (p == null ? "—" : `#${p}`);

function Sparkline({ points }) {
  // points: array of positions (1..100, null=off). Draw inverted so #1 is at top.
  const w = 120, h = 28, pad = 3;
  const vals = points.map((p) => (p == null ? 100 : Math.min(100, p)));
  if (vals.length === 0) return <span className="admin-row-meta">no data</span>;
  const stepX = vals.length > 1 ? (w - pad * 2) / (vals.length - 1) : 0;
  const y = (v) => pad + ((v - 1) / 99) * (h - pad * 2); // rank 1 → top
  const d = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(pad + i * stepX).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const last = vals[vals.length - 1];
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-hidden="true">
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
      <circle cx={(pad + (vals.length - 1) * stepX).toFixed(1)} cy={y(last).toFixed(1)} r="2.5" fill="var(--accent)" />
    </svg>
  );
}

// Per-stage display. A row's stage is its article status, or — before an article
// exists — its brief state (ready / held / writing). Buckets drive the filter tabs.
const STAGE_BADGE = {
  ready: "", held: "warm", writing: "warm", needs_review: "warm",
  draft: "warm", approved: "social_proof", published: "social_proof", rejected: "",
};
const STAGE_LABEL = {
  ready: "Ready", held: "Held", writing: "Writing…", needs_review: "Needs review",
  draft: "Draft · flagged", approved: "Approved", published: "Published", rejected: "Rejected",
};
const PIPE_FILTERS = [
  ["all", "All"], ["ready", "Ready"], ["writing", "Writing"], ["review", "Needs review"],
  ["approved", "Approved"], ["published", "Published"], ["held", "Held"], ["rejected", "Rejected"],
];
// Lower rank sorts to the top of "All" — most actionable first.
const BUCKET_RANK = { review: 0, writing: 1, ready: 2, approved: 3, held: 4, published: 5, rejected: 6 };

// "Writing…" is a transient client-side flag (the writer agent runs async and only
// later inserts the article). Persisted so a refresh mid-write keeps the state.
const WRITING_KEY = "seo_writing_brief_ids";
function loadWriting() {
  try { return new Set(JSON.parse(localStorage.getItem(WRITING_KEY) || "[]")); } catch { return new Set(); }
}
function persistWriting(set) {
  try { localStorage.setItem(WRITING_KEY, JSON.stringify([...set])); } catch { /* ignore quota */ }
}

// The review/preview modal: the article rendered, its §5 rubric (expertise, intent
// match, no-slop, …), its live rank, and the approve / reject / edit / publish
// actions — everything the old Article Review row exposed, now in context.
function ReviewModal({ row, busy, onClose, onAct, onEditSave, onPublish, onApproveAndPublish, publisherEnabled }) {
  const a = row.article;
  const perf = row.perf;
  const [editing, setEditing] = useState(null); // { content, original } | null
  if (!a) return null;
  const failed = a.quality_score?.gates && a.quality_score.passed === false;
  const isReview = a.status === "draft" || a.status === "needs_review";
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: "min(960px, 95vw)", maxWidth: "95vw" }}>
        <h3 className="modal-title">{a.title || "Article"}</h3>
        <div className="admin-row-meta" style={{ marginBottom: 8, flexWrap: "wrap" }}>
          <span className={`admin-badge ${STAGE_BADGE[a.status] || ""}`}>{STAGE_LABEL[a.status] || a.status}</span>
          {a.target_keyword && <span className="admin-badge">{a.target_keyword}</span>}
          <span className="admin-badge">{a.word_count ?? "—"} words</span>
          <span>/{a.slug}</span>
        </div>

        {editing ? (
          <>
            <p className="modal-message">Edits are logged as a lesson the writer learns from, then the article is approved.</p>
            <textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              style={{ width: "100%", minHeight: 340, fontFamily: "monospace", fontSize: 12 }} autoFocus />
          </>
        ) : (
          <>
            <iframe title="article preview" sandbox="" srcDoc={a.html_content || "<p>(no content)</p>"}
              style={{ width: "100%", height: "52vh", border: "1px solid var(--border)", borderRadius: "var(--r-control)", background: "#fff" }} />
            {a.quality_score?.gates && <QualityReport quality={a.quality_score} />}
            {perf && perf.snapshots > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="admin-row-meta" style={{ alignItems: "center" }}>
                  <span className={`admin-badge ${RANK_BADGE[perf.bucket] || ""}`}>{perf.bucket}</span>
                  <span className="admin-badge">{posLabel(perf.latest)}</span>
                  <span style={{ color: perf.delta > 0 ? "var(--success)" : perf.delta < 0 ? "var(--warn)" : undefined }}>
                    {perf.delta > 0 ? `▲ +${perf.delta}` : perf.delta < 0 ? `▼ ${perf.delta}` : "—"} since first snapshot
                  </span>
                </div>
                <Sparkline points={perf.positions} />
              </div>
            )}
          </>
        )}

        {!editing && !publisherEnabled && (isReview || a.status === "approved") && (
          <p className="admin-row-meta" style={{ color: "var(--warn)", fontWeight: 600, marginTop: 10 }}>
            ⚠ The Publisher is switched off in Controls — approving will not put this on the site.
          </p>
        )}

        <div className="modal-actions" style={{ flexWrap: "wrap" }}>
          {editing ? (
            <>
              <button className="secondary" onClick={() => setEditing(null)}>Cancel edit</button>
              <button className="btn" disabled={busy} onClick={() => onEditSave(a.id, editing.content, editing.original)}>Save &amp; approve</button>
            </>
          ) : (
            <>
              <button className="secondary" onClick={onClose}>Close</button>
              {isReview && (
                <>
                  <button className="secondary" disabled={busy}
                    onClick={() => setEditing({ content: a.html_content || "", original: a.html_content || "" })}>Edit HTML</button>
                  <button className="danger" disabled={busy} onClick={() => onAct(a.id, "rejected")}>Reject</button>
                  {/* "Approve only" keeps the approved-but-unpublished state
                      reachable — an unattended writer still needs that queue —
                      but it is no longer the path of least resistance. */}
                  <button className="secondary" disabled={busy} onClick={() => onAct(a.id, "approved")}>Approve only</button>
                  <button className="btn" disabled={busy} onClick={() => onApproveAndPublish(a)}>
                    {failed ? "Approve anyway & publish" : "Approve & publish"}
                  </button>
                </>
              )}
              {a.status === "approved" && (
                <>
                  <button className="secondary" disabled={busy} onClick={() => onAct(a.id, "draft")}>Back to draft</button>
                  <button className="btn" disabled={busy} onClick={() => onPublish(a)}>🚀 Publish</button>
                </>
              )}
              {a.status === "published" && a.blog_path && (
                <a className="admin-row-meta" href={blogUrl(a.blog_path)} target="_blank" rel="noreferrer">Open live page ↗</a>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ContentPipeline({ briefs, articles, performance, reloadBriefs, reloadArticles }) {
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [settings, setSettings] = useState(null);
  const [writing, setWriting] = useState(loadWriting);
  const [review, setReview] = useState(null);       // row currently open in the modal
  const [confirmDel, setConfirmDel] = useState(null);
  // Per-row outcome, keyed by row id. The single toolbar `msg` was the whole
  // problem in backlog item 136: a row action hundreds of pixels down the table
  // reported its result in muted grey at the top of the panel, so a failure was
  // indistinguishable from a dead button. Row actions now answer at the row.
  const [rowMsg, setRowMsg] = useState({});         // { [rowId]: { text, kind } }
  const note = (rowId, text, kind = "ok") => setRowMsg((m) => ({ ...m, [rowId]: { text, kind } }));
  /** runAgent() returns "Started …" on success and an error string otherwise. */
  const noteAgent = (rowId, m) => note(rowId, m, m.startsWith("Started") ? "ok" : "err");

  const batchN = (s) => (s?.auto_generate ? Math.max(1, Math.min(10, Number(s.auto_generate_count) || 1)) : 1);

  useEffect(() => {
    let alive = true;
    getSettings().then((d) => { if (alive) setSettings(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Latest article per brief, and rank snapshots per article.
  const artByBrief = new Map();
  for (const a of articles) if (a.brief_id && !artByBrief.has(a.brief_id)) artByBrief.set(a.brief_id, a);
  const perfByArticle = new Map();
  for (const p of performance) {
    if (!perfByArticle.has(p.article_id)) perfByArticle.set(p.article_id, []);
    perfByArticle.get(p.article_id).push(p);
  }
  const briefIds = new Set(briefs.map((b) => b.id));

  // Drop the "writing" flag once the article actually lands.
  useEffect(() => {
    setWriting((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) if (artByBrief.has(id)) { next.delete(id); changed = true; }
      if (changed) persistWriting(next);
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  // One row per brief (+ its article), plus any orphan articles (brief deleted).
  const rows = [];
  for (const b of briefs) rows.push({ id: b.id, brief: b, article: artByBrief.get(b.id) || null });
  for (const a of articles) if (!a.brief_id || !briefIds.has(a.brief_id)) rows.push({ id: `art-${a.id}`, brief: null, article: a });

  const bucketOf = (row) => {
    const a = row.article;
    if (a) return a.status === "needs_review" || a.status === "draft" ? "review" : a.status;
    if (row.brief && writing.has(row.brief.id)) return "writing";
    if (row.brief && row.brief.status === "held") return "held";
    return "ready";
  };
  const rowTime = (r) => new Date(r.article?.created_at || r.brief?.created_at || 0).getTime();

  const enriched = rows.map((r) => ({
    ...r,
    bucket: bucketOf(r),
    perf: r.article ? rankSummary(perfByArticle.get(r.article.id) || []) : null,
  }));
  enriched.sort((x, y) => (BUCKET_RANK[x.bucket] - BUCKET_RANK[y.bucket]) || (rowTime(y) - rowTime(x)));

  const counts = enriched.reduce((m, r) => ((m[r.bucket] = (m[r.bucket] || 0) + 1), m), {});
  const shown = filter === "all" ? enriched : enriched.filter((r) => r.bucket === filter);

  /* ── actions ── */
  async function writeArticle(b) {
    setBusy(true); note(b.id, "Starting…", "ok");
    const m = await runAgent(
      WRITER_AGENT, `Write article for brief: ${b.working_title || b.id}`,
      `Write ONE article for THIS SPECIFIC brief — do not pick a different one. Read it with `
      + `article_tools.py brief ${b.id}, write the 1,500–2,000 word body per AGENTS.md, self-grade the `
      + `rubric into meta.json, then save with article_tools.py save ${b.id} body.html meta.json. It lands `
      + `in Needs review (or Draft if a hard gate fails).`,
    );
    if (m.startsWith("Started")) {
      setWriting((prev) => { const n = new Set(prev).add(b.id); persistWriting(n); return n; });
    }
    setBusy(false); noteAgent(b.id, m);
  }
  function cancelWriting(id) {
    setWriting((prev) => { const n = new Set(prev); n.delete(id); persistWriting(n); return n; });
  }
  async function generateBriefs() {
    setBusy(true); setMsg(null);
    const m = await runAgent(
      BRIEF_AGENT, "Generate content briefs",
      "Run your routine per HEARTBEAT.md: for the top approved keywords (brief_tools.py queue), "
      + "read context and write briefs (outline + internal links + 2–4 first-hand expertise angles), then save each.",
    );
    setBusy(false); setMsg(m);
  }
  async function generateBatch() {
    setBusy(true); setMsg(null);
    const cfg = await getSettings().catch(() => settings);
    if (cfg) setSettings(cfg);
    const n = batchN(cfg);
    const m = n > 1
      ? await runAgent(
          WRITER_AGENT, `Write ${n} SEO articles`,
          `Run your routine per HEARTBEAT.md as a batch. Process the next ${n} ready briefs from the `
          + "article_tools.py queue one at a time: read each (article_tools.py brief <id>), write the "
          + "1,500–2,000 word body per AGENTS.md, self-grade the rubric into meta.json, and save it before "
          + `moving to the next. Stop after ${n} articles or when no ready briefs remain. Each lands in `
          + "Needs review (or Draft if a hard gate fails).",
        )
      : await runAgent(
          WRITER_AGENT, "Write an SEO article",
          "Run your routine per HEARTBEAT.md: pick the next ready brief (article_tools.py queue), "
          + "read it (article_tools.py brief <id>), write the 1,500–2,000 word body per AGENTS.md, self-grade the "
          + "rubric into meta.json, and save. It lands in Needs review (or Draft if a hard gate fails).",
        );
    setBusy(false); setMsg(m);
  }
  async function refreshRanks() {
    setBusy(true); setMsg(null);
    const m = await runAgent(RANK_AGENT, "Track article ranks",
      "Run rank_tools.py snapshot all per HEARTBEAT.md, then rank_tools.py stuck. Report movers + stuck pages.");
    setBusy(false); setMsg(m);
  }
  async function act(id, status) {
    setBusy(true);
    try { await setArticleStatus(id, status); setReview(null); await reloadArticles(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  /**
   * Hand the article to the Publisher agent. `rowId` is where the outcome is
   * reported — publishing usually starts from the modal, which closes, so the
   * answer has to land on the row the user came from (item 136).
   */
  async function runPublish(a, rowId) {
    const m = await runAgent(PUBLISH_AGENT, `Publish article: ${a.title}`,
      `Run publish_tools.py publish ${a.id} per HEARTBEAT.md. It writes Website/public/blog/${a.slug}.html and refreshes the index. Report the path.`);
    noteAgent(rowId, m);
    return m;
  }

  async function publish(a, rowId) {
    setBusy(true);
    setReview(null);
    await runPublish(a, rowId ?? `art-${a.id}`);
    setBusy(false);
  }

  /**
   * Item 140 — Ryan: "why isn't the approve button the publish button?"
   * Approving and publishing were two clicks by the same person for no added
   * judgement. This does both. `approved` survives as a DB state (an unattended
   * writer still needs a review queue) and "Approve only" still reaches it;
   * `publisher_enabled` remains the kill switch, and when it's off this stops
   * after approving and SAYS SO rather than silently doing half the job.
   */
  async function approveAndPublish(a, rowId) {
    setBusy(true);
    const key = rowId ?? `art-${a.id}`;
    try {
      await setArticleStatus(a.id, "approved");
      setReview(null);
      if (!settings?.publisher_enabled) {
        note(key, "Approved, but NOT published — the Publisher is switched off in Controls above.", "err");
      } else {
        await runPublish(a, key);
      }
      await reloadArticles();
    } catch (e) {
      note(key, `Could not approve: ${e.message}`, "err");
    } finally { setBusy(false); }
  }
  async function saveEdit(id, content, original) {
    setBusy(true);
    try {
      if (content !== original) {
        await recordArticleLesson({ article_id: id, original_text: original, edited_text: content });
      }
      await updateArticleContent(id, content);
      await setArticleStatus(id, "approved");
      setReview(null); await reloadArticles();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function setBrief(id, status) {
    setBusy(true);
    try { await setBriefStatus(id, status); await reloadBriefs(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function doDelete(id) {
    setBusy(true);
    try { await deleteBrief(id); setConfirmDel(null); await reloadBriefs(); } catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  return (
    <section className="admin-card" style={{ gridColumn: "1 / -1" }}>
      <div className="admin-panel-head"><h2>Content Pipeline</h2>
        <span className="admin-panel-count">
          {counts.ready || 0} ready · {counts.review || 0} to review · {counts.approved || 0} approved · {counts.published || 0} live
        </span>
      </div>

      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button className="secondary" onClick={generateBriefs} disabled={busy}>{busy ? "Starting…" : "📝 Generate briefs"}</button>
        <button className="secondary" onClick={generateBatch} disabled={busy}>
          {busy ? "Starting…" : (batchN(settings) > 1 ? `✍️ Write next ${batchN(settings)}` : "✍️ Write next ready")}
        </button>
        <button className="secondary" onClick={refreshRanks} disabled={busy}>📈 Refresh ranks</button>
        {msg && <span className="admin-panel-count" style={{ color: "var(--muted)" }}>{msg}</span>}
      </div>

      <div className="admin-tabs">
        {PIPE_FILTERS.map(([key, label]) => {
          const n = key === "all" ? enriched.length : (counts[key] || 0);
          return (
            <button key={key} className={`admin-tab${filter === key ? " active" : ""}`} onClick={() => setFilter(key)}>
              {label} ({n})
            </button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <div className="admin-empty">
          {filter === "ready"
            ? "No ready briefs. Approve keywords in the Keyword Queue, then Generate briefs."
            : `Nothing in "${(PIPE_FILTERS.find(([k]) => k === filter) || [null, "this filter"])[1]}".`}
        </div>
      ) : (
        <div className="admin-list">
          {shown.map((r) => {
            const b = r.brief, a = r.article, perf = r.perf;
            const stage = a ? a.status : (b && writing.has(b.id) ? "writing" : (b?.status || "ready"));
            const title = a?.title || b?.working_title || "(untitled)";
            return (
              <div className="admin-row" key={r.id}>
                <div className="admin-row-top">
                  <span className="admin-row-title">{title}</span>
                  <span className="admin-row-meta" style={{ flexWrap: "wrap" }}>
                    <span className={`admin-badge ${STAGE_BADGE[stage] || ""}`}>{STAGE_LABEL[stage] || stage}</span>
                    {a?.target_keyword && <span className="admin-badge">{a.target_keyword}</span>}
                    {a ? <span className="admin-badge">{a.word_count ?? "—"}w</span>
                       : b && <span className="admin-badge">~{b.target_word_count ?? "—"}w</span>}
                    {perf && perf.snapshots > 0 && <span className={`admin-badge ${RANK_BADGE[perf.bucket] || ""}`}>{posLabel(perf.latest)}</span>}
                  </span>
                </div>
                <div className="admin-row-meta">
                  {a ? (
                    <>
                      <span>/{a.slug}</span>
                      {perf && perf.snapshots > 0 && (
                        <span style={{ color: perf.delta > 0 ? "var(--success)" : perf.delta < 0 ? "var(--warn)" : undefined }}>
                          {perf.delta > 0 ? `▲ +${perf.delta}` : perf.delta < 0 ? `▼ ${perf.delta}` : "tracking"}
                        </span>
                      )}
                      {a.published_at && <span>published {fmtDate(a.published_at)}</span>}
                    </>
                  ) : b ? (
                    <>
                      <span>{(b.outline || []).length} H2</span>
                      <span>{(b.expertise_prompts || []).length} angles</span>
                      <span>{(b.internal_links || []).length} links</span>
                      <span>{fmtDate(b.created_at)}</span>
                    </>
                  ) : null}
                </div>
                {perf && perf.snapshots > 0 && <Sparkline points={perf.positions} />}

                <div className="admin-row-actions">
                  {a ? (
                    <>
                      {/* Item 139 — the row used to say "Preview & review" whatever
                          state the article was in, so an approved article
                          advertised the step already done. The label now names
                          the NEXT step, and an approved article can be published
                          from the row instead of only from inside the modal. */}
                      {a.status === "approved" ? (
                        <>
                          <button className="btn" disabled={busy} onClick={() => publish(a, r.id)}>🚀 Publish</button>
                          <button className="secondary" onClick={() => setReview(r)}>👁 Preview</button>
                        </>
                      ) : (
                        <button className="secondary" onClick={() => setReview(r)}>
                          {a.status === "draft" || a.status === "needs_review" ? "👁 Review & publish" : "👁 Preview"}
                        </button>
                      )}
                      {a.status === "published" && a.blog_path && (
                        <a className="admin-row-meta" href={blogUrl(a.blog_path)} target="_blank" rel="noreferrer">Open ↗</a>
                      )}
                    </>
                  ) : b && writing.has(b.id) ? (
                    <>
                      <button className="btn" disabled>✍️ Writing…</button>
                      <button className="secondary" onClick={() => cancelWriting(b.id)} title="Didn’t start? Clear this flag.">Reset</button>
                    </>
                  ) : b ? (
                    <>
                      <button className="btn" disabled={busy} onClick={() => writeArticle(b)}>✍️ Write article</button>
                      {b.status === "held"
                        ? <button className="secondary" disabled={busy} onClick={() => setBrief(b.id, "ready")}>Resume</button>
                        : <button className="secondary" disabled={busy} onClick={() => setBrief(b.id, "held")}>Postpone</button>}
                      <button className="danger" disabled={busy} onClick={() => setConfirmDel(b)}>Delete</button>
                    </>
                  ) : null}
                </div>
                {rowMsg[r.id] && (
                  <div
                    className="admin-row-meta"
                    style={{
                      marginTop: 6,
                      color: rowMsg[r.id].kind === "err" ? "var(--warn)" : "var(--muted)",
                      fontWeight: rowMsg[r.id].kind === "err" ? 600 : undefined,
                    }}
                  >
                    {rowMsg[r.id].kind === "err" ? "⚠ " : ""}{rowMsg[r.id].text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {review && (
        <ReviewModal row={review} busy={busy} onClose={() => setReview(null)}
          onAct={act} onEditSave={saveEdit}
          onPublish={(a) => publish(a, review.id)}
          onApproveAndPublish={(a) => approveAndPublish(a, review.id)}
          publisherEnabled={!!settings?.publisher_enabled} />
      )}
      {confirmDel && (
        <div className="modal-overlay" onClick={() => setConfirmDel(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Delete this brief?</h3>
            <p className="modal-message">
              “{confirmDel.working_title || "(untitled brief)"}” will be permanently removed. Its keyword stays in the
              queue — you can re-brief it later. This can’t be undone.
            </p>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="danger" disabled={busy} onClick={() => doDelete(confirmDel.id)}>Delete brief</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── (removed: Article Queue, Performance) ──────────── */
/* ──────────────────────────────── View ───────────────────────────────────── */
export default function SeoRankView() {
  const [clusters, setClusters] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [audits, setAudits] = useState([]);
  const [articles, setArticles] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [meter, setMeter] = useState(null);
  const [err, setErr] = useState(null);

  const reloadClusters = useCallback(async () => { try { setClusters(await listClusters()); } catch (e) { setErr(e.message); } }, []);
  const reloadKeywords = useCallback(async () => { try { setKeywords(await listKeywords()); } catch (e) { setErr(e.message); } }, []);
  const reloadBriefs = useCallback(async () => { try { setBriefs(await listBriefs()); } catch (e) { setErr(e.message); } }, []);
  const reloadAudits = useCallback(async () => { try { setAudits(await listAudits()); } catch (e) { setErr(e.message); } }, []);
  const reloadArticles = useCallback(async () => { try { setArticles(await listArticles()); } catch (e) { setErr(e.message); } }, []);
  const reloadPerformance = useCallback(async () => { try { setPerformance(await listPerformance()); } catch (e) { setErr(e.message); } }, []);
  const reloadMeter = useCallback(async () => { try { setMeter(await getCostMeter()); } catch (e) { console.error(e); } }, []);

  useEffect(() => { reloadClusters(); reloadKeywords(); reloadBriefs(); reloadAudits(); reloadArticles(); reloadPerformance(); reloadMeter(); },
    [reloadClusters, reloadKeywords, reloadBriefs, reloadAudits, reloadArticles, reloadPerformance, reloadMeter]);

  useEffect(() => {
    const ch = supabase
      .channel("admin-seo")
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_keywords" }, () => { reloadKeywords(); reloadMeter(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_briefs" }, () => { reloadBriefs(); reloadKeywords(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_articles" }, reloadArticles)
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_performance" }, () => { reloadPerformance(); reloadMeter(); })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [reloadKeywords, reloadBriefs, reloadArticles, reloadPerformance, reloadMeter]);

  return (
    <AdminLayout title="SEO Rank" subtitle="Cluster-driven keyword research, on-page audits, and the blog article engine.">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="secondary" onClick={() => window.open("/docs/seo-engine-guide.html", "_blank", "noopener")}>
          📖 How to use this
        </button>
      </div>
      {err && <div className="admin-banner" role="alert"><span>{err}</span></div>}
      <Controls meter={meter} />
      <div className="admin-panel-grid">
        <KeywordQueue keywords={keywords} clusters={clusters} reload={reloadKeywords} />
        <AuditPanel audits={audits} />
        <ContentPipeline
          briefs={briefs} articles={articles} performance={performance}
          reloadBriefs={reloadBriefs} reloadArticles={reloadArticles}
        />
      </div>
    </AdminLayout>
  );
}
